import api, { auth } from './api';

// Supported currencies (PHP only for Agos)
export type Currency = 'PHP';

// Payment method types - QRPh (InstaPay), GCash, and Maya
export type PaymentMethodType = 'qrph' | 'gcash' | 'maya';

export interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  phone?: string;  // For e-wallets
}

export interface PaymentIntent {
  id: string;
  userId: string;
  amount: number;
  currency: Currency;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'awaiting_payment';
  paymentMethod: PaymentMethodType | null;
  description: string | null;
  metadata: Record<string, any>;
  externalId: string | null;
  checkoutUrl?: string;  // PayMongo checkout URL
  createdAt: Date;
  updatedAt: Date;
}

// Verification types
export type VerificationType = 'basic' | 'enhanced';

// Credit-based pricing model:
// Users purchase credit bundles (₱500-₱5,000) and use 1 credit per verification.
// Credit value per verification:
// - Basic: OCR + Liveness + Audit Trail (1 Basic credit = ₱25 value)
// - Enhanced: Basic + PhilSys QR check (1 Enhanced credit = ₱45 value)
// Effective price with bundles: ₱19-25 per verification (up to 30% bonus credits)
export const PRICING_TIERS = {
  standard: {
    basicPhp: 25,
    basicUsd: 0.45,
    enhancedPhp: 45,
    enhancedUsd: 0.80,
    volumeMin: 1,
    volumeMax: 99,
    discount: 0,
    label: 'Standard',
  },
  business: {
    basicPhp: 21,
    basicUsd: 0.38,
    enhancedPhp: 38,
    enhancedUsd: 0.68,
    volumeMin: 100,
    volumeMax: 999,
    discount: 0.15,
    label: 'Business (15% off)',
  },
  enterprise: {
    basicPhp: 19,
    basicUsd: 0.34,
    enhancedPhp: 34,
    enhancedUsd: 0.60,
    volumeMin: 1000,
    volumeMax: Infinity,
    discount: 0.25,
    label: 'Enterprise (25% off)',
  },
};

// Enterprise high-volume pricing for banks/fintechs/telcos (PHP) - contact sales
export const ENTERPRISE_VOLUME_PRICING = {
  tier1: { minVolume: 10000, maxVolume: 50000, basicPrice: 17, enhancedPrice: 38, savings: 0.15 },
  tier2: { minVolume: 50001, maxVolume: 100000, basicPrice: 15, enhancedPrice: 34, savings: 0.25 },
  tier3: { minVolume: 100001, maxVolume: 500000, basicPrice: 13, enhancedPrice: 30, savings: 0.35 },
  tier4: { minVolume: 500001, maxVolume: Infinity, basicPrice: 10, enhancedPrice: 25, savings: 0.45 },
};

// Credit Bundle Pricing Model (₱100 - ₱5,000)
// Users purchase bundles FIRST, then use credits for verifications
// Each verification type uses different credit amounts:
// - Basic: 1 credit (₱25 value)
// - Enhanced: 1 credit (₱45 value)
export interface CreditBundle {
  id: string;
  name: string;
  price: number; // PHP
  basicCredits: number; // Credits for Basic verifications
  enhancedCredits: number; // Credits for Enhanced verifications
  bonusPercent: number; // Bonus credit percentage
  popular?: boolean;
  badge?: string; // e.g., "Popular", "Best Value"
}

export const CREDIT_BUNDLES: CreditBundle[] = [
  {
    id: 'personal',
    name: 'Personal',
    price: 100,
    basicCredits: 4,
    enhancedCredits: 2,
    bonusPercent: 0,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 500,
    basicCredits: 20,
    enhancedCredits: 11,
    bonusPercent: 0,
    popular: true,
    badge: 'Popular',
  },
  {
    id: 'business',
    name: 'Business',
    price: 2500,
    basicCredits: 120,
    enhancedCredits: 66,
    bonusPercent: 0.20, // +20% bonus credits
    badge: 'Best Value',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 5000,
    basicCredits: 260,
    enhancedCredits: 143,
    bonusPercent: 0.30, // +30% bonus credits
  },
];

class PaymentService {
  private currency: Currency = 'PHP';

  getCurrency(): Currency {
    return this.currency;
  }

  // Format currency for display (PHP only)
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount);
  }

  /**
   * Create a payment intent
   * @param amount Amount in centavos (e.g., 2000 = ₱20)
   * @param description Payment description
   * @param metadata Additional metadata
   */
  async createPaymentIntent(
    amount: number,
    description?: string,
    metadata?: Record<string, any>
  ): Promise<PaymentIntent> {
    const response = await api.request('/api/payments/intent', {
      method: 'POST',
      body: JSON.stringify({
        amount,
        currency: 'PHP',
        description,
        metadata,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create payment intent');
    }

    return response.data;
  }

  /**
   * Create checkout session for GCash or Maya
   * Returns checkout URL to redirect user to
   * @param paymentIntentId Payment intent ID
   * @param paymentMethod 'gcash' or 'maya'
   * @param successUrl URL to redirect to on success
   * @param failureUrl URL to redirect to on failure
   */
  async createCheckoutSession(
    paymentIntentId: string,
    paymentMethod: PaymentMethodType,
    successUrl: string,
    failureUrl: string
  ): Promise<{ paymentId: string; checkoutUrl: string; status: string }> {
    const response = await api.request(`/api/payments/${paymentIntentId}/checkout`, {
      method: 'POST',
      body: JSON.stringify({
        paymentMethod,
        successUrl,
        failureUrl,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create checkout session');
    }

    return response.data;
  }

  /**
   * Get payment status
   * @param paymentId Payment ID
   */
  async getPaymentStatus(paymentId: string): Promise<{
    id: string;
    status: string;
    amount: number;
    currency: string;
    paymentMethod: PaymentMethodType | null;
  }> {
    const response = await api.request(`/api/payments/${paymentId}/status`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to get payment status');
    }

    return response.data;
  }

  /**
   * Get payment by ID
   * @param paymentId Payment ID
   */
  async getPaymentById(paymentId: string): Promise<PaymentIntent> {
    const response = await api.request(`/api/payments/${paymentId}`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to get payment');
    }

    return response.data;
  }

  /**
   * Get user's payments
   * @param limit Number of payments to fetch
   * @param offset Offset for pagination
   */
  async getUserPayments(limit: number = 20, offset: number = 0): Promise<PaymentIntent[]> {
    const response = await api.request(`/api/payments?limit=${limit}&offset=${offset}`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to get payments');
    }

    return response.data;
  }

  /**
   * Calculate verification cost - Two-tier model (Basic vs Enhanced)
   * Returns amount in centavos for PayMongo
   */
  calculateVerificationCost(
    fileCount: number,
    verificationType: VerificationType = 'basic'
  ): { total: number; pricePerVerification: number; tier: string; verificationType: VerificationType } {
    if (fileCount <= 0) {
      return { total: 0, pricePerVerification: 0, tier: 'none', verificationType };
    }

    const tierKey = this.getPricingTier(fileCount);
    const tier = PRICING_TIERS[tierKey];

    const pricePerVerification = verificationType === 'enhanced' ? tier.enhancedPhp : tier.basicPhp;

    return {
      total: pricePerVerification * fileCount,
      pricePerVerification,
      tier: tierKey,
      verificationType,
    };
  }

  // Calculate enterprise pricing for banks/fintechs
  calculateEnterpriseCost(
    monthlyVolume: number,
    verificationType: VerificationType = 'basic'
  ): {
    pricePerVerification: number;
    totalMonthlyCost: number;
    tier: string;
    verificationType: VerificationType;
  } {
    let tier: string;
    let pricePerVerification: number;

    if (monthlyVolume >= ENTERPRISE_VOLUME_PRICING.tier4.minVolume) {
      tier = 'tier4';
      pricePerVerification = verificationType === 'enhanced'
        ? ENTERPRISE_VOLUME_PRICING.tier4.enhancedPrice
        : ENTERPRISE_VOLUME_PRICING.tier4.basicPrice;
    } else if (monthlyVolume >= ENTERPRISE_VOLUME_PRICING.tier3.minVolume) {
      tier = 'tier3';
      pricePerVerification = verificationType === 'enhanced'
        ? ENTERPRISE_VOLUME_PRICING.tier3.enhancedPrice
        : ENTERPRISE_VOLUME_PRICING.tier3.basicPrice;
    } else if (monthlyVolume >= ENTERPRISE_VOLUME_PRICING.tier2.minVolume) {
      tier = 'tier2';
      pricePerVerification = verificationType === 'enhanced'
        ? ENTERPRISE_VOLUME_PRICING.tier2.enhancedPrice
        : ENTERPRISE_VOLUME_PRICING.tier2.basicPrice;
    } else {
      tier = 'tier1';
      pricePerVerification = verificationType === 'enhanced'
        ? ENTERPRISE_VOLUME_PRICING.tier1.enhancedPrice
        : ENTERPRISE_VOLUME_PRICING.tier1.basicPrice;
    }

    return {
      pricePerVerification,
      totalMonthlyCost: pricePerVerification * monthlyVolume,
      tier,
      verificationType,
    };
  }

  // Get pricing tier for a given volume
  getPricingTier(fileCount: number): keyof typeof PRICING_TIERS {
    if (fileCount >= 1000) return 'enterprise';
    if (fileCount >= 100) return 'business';
    return 'standard';
  }

  /**
   * Get price for single verification in PHP
   */
  getVerificationPrice(verificationType: VerificationType = 'basic'): number {
    const tier = PRICING_TIERS.standard;
    return verificationType === 'enhanced' ? tier.enhancedPhp : tier.basicPhp;
  }

  /**
   * Get available payment methods (QRPh, GCash, and Maya)
   */
  getAvailablePaymentMethods(): PaymentMethodType[] {
    return ['qrph', 'gcash', 'maya'];
  }

  /**
   * Get all available credit bundles
   */
  getAvailableBundles(): CreditBundle[] {
    return CREDIT_BUNDLES;
  }

  /**
   * Get a specific bundle by ID
   */
  getBundleById(bundleId: string): CreditBundle | undefined {
    return CREDIT_BUNDLES.find(b => b.id === bundleId);
  }

  /**
   * Get the popular/recommended bundle
   */
  getPopularBundle(): CreditBundle | undefined {
    return CREDIT_BUNDLES.find(b => b.popular);
  }

  /**
   * Calculate effective price per verification for a bundle
   */
  calculateBundleEffectivePrice(bundle: CreditBundle, verificationType: VerificationType): number {
    const credits = verificationType === 'enhanced' ? bundle.enhancedCredits : bundle.basicCredits;
    return credits > 0 ? bundle.price / credits : 0;
  }

  /**
   * Calculate savings percentage for a bundle
   */
  calculateBundleSavingsPercent(bundle: CreditBundle, verificationType: VerificationType): number {
    const standardPrice = verificationType === 'enhanced'
      ? PRICING_TIERS.standard.enhancedPhp
      : PRICING_TIERS.standard.basicPhp;
    const effectivePrice = this.calculateBundleEffectivePrice(bundle, verificationType);
    return ((standardPrice - effectivePrice) / standardPrice) * 100;
  }

  /**
   * Calculate total savings amount for a bundle
   */
  calculateBundleSavingsAmount(bundle: CreditBundle, verificationType: VerificationType): number {
    const credits = verificationType === 'enhanced' ? bundle.enhancedCredits : bundle.basicCredits;
    const standardPrice = verificationType === 'enhanced'
      ? PRICING_TIERS.standard.enhancedPhp
      : PRICING_TIERS.standard.basicPhp;
    const totalStandardCost = standardPrice * credits;
    return totalStandardCost - bundle.price;
  }

  /**
   * Convert PHP amount to centavos for PayMongo
   */
  toCentavos(amountPhp: number): number {
    return Math.round(amountPhp * 100);
  }

  /**
   * Convert centavos to PHP for display
   */
  fromCentavos(centavos: number): number {
    return centavos / 100;
  }
}

export const paymentService = new PaymentService();
